import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, PageHeader, Picture, QuoteForm } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiftingCategoryItemsPage() {
  const { categoryId } = useParams();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/gifting-categories`),
      axios.get(`${API}/gifting-items`),
      axios.get(`${API}/colors`),
    ]).then(([cats, its, cols]) => {
      setCategory(cats.data.find((c) => c.id === categoryId) || null);
      setItems(its.data.filter((i) => i.category_id === categoryId));
      setColors(cols.data);
    }).catch(() => setCategory(null)).finally(() => setLoading(false));
  }, [categoryId]);

  const colorOf = (id) => colors.find((c) => c.id === id);

  if (loading) {
    return <div className="min-h-screen bg-paper"><Navbar /><div className="pt-40 pb-32 text-center text-graphite" role="status">Loading…</div><Footer /></div>;
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-paper">
        <Navbar />
        <PageHeader title="We couldn't find that category" lede="It may have been renamed. The full gifting catalogue is one click away.">
          <Link to="/corporate-gifting" className="btn btn-ink">Back to corporate gifting</Link>
        </PageHeader>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="gifting-category-items-page" className="min-h-screen bg-paper">
      <Navbar />
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Corporate gifting", to: "/corporate-gifting" }, { label: category.name }]}
        title={category.name}
        lede={category.description}
      />

      <main>
        <section className="py-16 md:py-20">
          <Container>
            <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
              <p className="text-graphite"><span className="font-display font-bold text-ink text-xl">{items.length}</span> {items.length === 1 ? "item" : "items"}</p>
              <Link to="/corporate-gifting" className="ink-link font-display font-semibold text-ink">All gifting categories</Link>
            </div>

            {items.length === 0 ? (
              <div className="border border-dashed border-ink p-10 text-center">
                <p className="font-display font-semibold text-xl text-ink">Nothing listed here yet.</p>
                <p className="mt-2 text-graphite">Send an enquiry below and we'll share what's available in {category.name.toLowerCase()}.</p>
              </div>
            ) : (
              <ul className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {items.map((item) => (
                  <li key={item.id}>
                    <Link to={`/corporate-gifting/item/${item.id}`} data-testid={`gifting-item-${item.id}`}
                      className="group flex h-full flex-col bg-stock border border-ink transition-[transform,box-shadow] duration-150 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#E5007D]">
                      <Picture src={item.images?.[0]} alt={item.name} className="w-full aspect-square border-b border-ink" />
                      <span className="p-5 flex flex-1 flex-col">
                        <span className="font-display font-bold text-lg text-ink">{item.name}</span>
                        {item.description && <span className="mt-1 text-sm text-graphite line-clamp-2">{item.description}</span>}
                        <span className="mt-auto pt-5 flex items-end justify-between gap-3">
                          <span className="font-display text-ink"><span className="text-sm text-graphite">From </span><span className="text-xl font-bold">₹{item.starting_price}</span></span>
                          {item.available_colors?.length > 0 && (
                            <span className="flex -space-x-1" aria-label={`${item.available_colors.length} colours`}>
                              {item.available_colors.slice(0, 5).map((cid) => {
                                const c = colorOf(cid);
                                return c ? <span key={cid} title={c.name} className="w-5 h-5 rounded-full border border-ink" style={{ backgroundColor: c.color_code }} /> : null;
                              })}
                            </span>
                          )}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Container>
        </section>

        <section id="enquiry-form" className="bg-stock border-t border-ink py-16 md:py-24">
          <Container className="grid gap-10 lg:grid-cols-[1fr_minmax(0,32rem)] lg:gap-16 items-start">
            <div>
              <h2 className="font-display font-bold text-3xl sm:text-5xl text-ink">Enquire about {category.name.toLowerCase()}</h2>
              <p className="mt-4 text-lg text-graphite leading-relaxed max-w-md">Quantity, customisation and budget help us quote accurately.</p>
            </div>
            <QuoteForm
              testid="category-enquiry-form"
              title=""
              service="Corporate Gifting"
              lockService
              messagePrefix={`Category: ${category.name}`}
              messageLabel="Quantity, customisation and budget"
              messagePlaceholder="e.g. 60 pieces, logo on front, under ₹500 each"
              submitLabel="Send enquiry"
            />
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
